import _ from "lodash";
import { fieldVisibilitySettings } from "../../../app/core/util/fieldVisibility";
import { toolTip } from "../../controls/toolTip";
import { alert } from "../../core/alert";
import { ll } from "../../core/ll";
import { Router } from "../../core/router";
import { settings } from "../../core/settings";
import { user } from "../../core/user";
import { schemas } from "../../data/schemas";

let availableId = 1;

export function getAvailableId(): number {
    return availableId++;
}

export interface RightLockable {
    rightLocked?: boolean;
}

export interface ComponentOptions {
    type?: string;
    id?: string;
    bindValue?: string | { type: string, value: string };
    classes?: string;
    containerClasses?: string;
    disabled?: boolean | "true";
    zScreen?: any;
    zData?: any;

    screenEntityType?: string;
    screenName?: string;
    sectionName?: string;
    subType?: string;
    /** Set to true if the component should be enabled even when its container is disabled because of field visibility settings. */
    excludeFromRecursiveDisable?: boolean;
}

// It would be good to remove this interface and move related functionality
// into the components that actually use it.
interface CommonComponentOptions extends ComponentOptions {
    iconId?: string;
    editableInputBind?: string | { type: string, value: string };
    required?: boolean | "true" | "false";
    bsc?: string;
    childBsc?: string;
    inputBsc?: string;
    childInputBsc?: string;
    labelBsc?: string;
    childLabelBsc?: string;
    maxLength?: number | string;
    field?: string;
    searchType?: string | any;
    entityType?: string;
    subtype?: string;
}

export abstract class Component {

    id: string;
    content: { [property: string]: Component[] } = {};
    options: CommonComponentOptions;
    contentRoot: Component;
    zData: any;
    recursiveDisable: boolean;
    router?: Router;

    constructor(options: ComponentOptions) {
        this.options = options;
        this.options.type = this.constructor.name;
    }

    render(selector: JQuery | string, method: "append" | "prepend" | "after" | "before" = "append"): void {
        if (typeof selector == "string") {
            selector = $(selector);
        }

        this.preRenderComponent();
        var html = this.generate();
        selector[method](html);
        this.initComponent();
    }

    preRenderComponent(): void {
        const options = this.options;

        options.id = options.id || getAvailableId().toString();
        options.zScreen = options.zScreen || {};

        if (options.zScreen.id) {
            options.id = options.zScreen.id + "_" + options.id;

            if (options.iconId) {
                options.iconId = options.zScreen.id + "_" + options.iconId;
            }
        }
        this.id = options.id;

        options.classes = options.classes || "";
        options.disabled = options.disabled || false;
        this.zData = options.zData || null;

        this.applyFieldVisibilitySettings();
        this.preRender();

        _.forEach(this.content, components => {
            _.forEach(components, child => {
                _.defaults(child.options, {
                    screenEntityType: options.screenEntityType,
                    screenName: options.screenName,
                    sectionName: options.sectionName,
                    subType: options.subType
                });

                if (this.recursiveDisable && !child.options.excludeFromRecursiveDisable) {
                    child.recursiveDisable = child.recursiveDisable || this.recursiveDisable;
                    child.options.disabled = child.recursiveDisable;
                }

                if (!child.options.bsc && options.childBsc) {
                    child.options.bsc = options.childBsc;
                    child.options.childBsc = options.childBsc;
                }

                if (!child.options.inputBsc && this.options.childInputBsc) {
                    child.options.inputBsc = this.options.childInputBsc;
                    child.options.childInputBsc = this.options.childInputBsc;
                }

                if (!child.options.labelBsc && this.options.childLabelBsc) {
                    child.options.labelBsc = this.options.childLabelBsc;
                    child.options.childLabelBsc = this.options.childLabelBsc;
                }

                child.options.zScreen = this.options.zScreen;
                child.contentRoot = this.contentRoot;
                child.router = this.router;

                child.preRenderComponent();
            });
        });

        if (options.screenEntityType) {
            options.zScreen.screenEntityType = options.screenEntityType;
        }

        if (!options.bindValue && options.editableInputBind) {
            options.bindValue = options.editableInputBind;
        }

        if (options.required == "true") {
            options.required = true;
        } else if (options.required == "false") {
            options.required = false;
        }

        if (options.bindValue && typeof options.bindValue == "string" && options.zScreen && schemas[options.zScreen.screenEntityType]) {
            options.screenEntityType = options.zScreen.screenEntityType;
            var screenEntityType = options.zScreen.screenEntityType;

            var prop = schemas[screenEntityType].Properties;
            var bindValue;
            if (options.bindValue.contains(":")) {
                bindValue = options.bindValue.substring(options.bindValue.indexOf(": ") + 2);
            } else {
                bindValue = options.bindValue;
            }

            var schemaValidation = schemas[screenEntityType].Properties[bindValue];
            if (!schemaValidation) {
                $.each(schemas[screenEntityType].NavigationProperties, function (index, value) {
                    var newBindValue;
                    if (bindValue.contains(".")) {
                        newBindValue = bindValue.split(".");
                        var newEntity = newBindValue[0];
                        var newProp = newBindValue[newBindValue.length - 1];
                        if (value.Type == newEntity) {
                            schemaValidation = schemas[value.Type].Properties[newProp];
                        }
                    } else {
                        schemaValidation = schemas[value.Type].Properties[bindValue];
                    }

                    if (schemaValidation) {
                        if ((!options.required == true) && (newBindValue && newBindValue.length == 2 && newBindValue[1] == "Value" && schemaValidation.Required && options.required != false)) {
                            options.required = false;
                        }
                        return;
                    }
                });
            }

            if (schemaValidation) {
                if ((schemaValidation.Required || schemaValidation.Display == "primary") && options.required != false) {
                    options.required = true;
                    delete schemas[screenEntityType].NavigationProperties[bindValue.toString().substring(bindValue.length - 2, bindValue.length)];
                }

                if (schemaValidation.MaxLength && !options.maxLength) {
                    options.maxLength = schemaValidation.MaxLength;
                }
            }
        }

        if ((options.entityType == "Division" ||
            options.searchType == "Division" ||
            options.entityType == "Region" ||
            options.searchType == "Region") &&
            !(user && user.companyData && user.companyData.DivisionAndRegionEnabled) &&
            options.field != "Region") {
            options.containerClasses += " hide-division-and-region";
            options.classes += " hide-division-and-region";
        }
    }

    abstract generate(): string;

    generateContent(): { [property: string]: string } {
        var results: { [property: string]: string } = {};

        _.forEach(this.content, (components, property) => {
            results[property] = this.generateContentForComponents(components);
        });

        return results;
    }

    generateContentForComponents(components: Component[]): string {
        var html = [];
        _.forEach(components, child => {
            html.push(child.generate());
        });
        return html.join("");
    }

    initComponent(): void {
        this.create();

        $("#" + this.id).data("me", this);
        if (this.zData) {
            $("#" + this.id).data("zData", this.zData);
        }

        _.forEach(this.content, components => {
            _.forEach(components, child => {
                child.initComponent();
            });
        });
    }

    destroyComponent(): void {
        _.forEach(this.content, components => {
            _.forEach(components, child => {
                child.destroyComponent();
            });
        });

        this.destroy();
    }

    get(selector: string): JQuery {
        var me = this,
            container = null;

        if (selector.indexOf("#") > -1) {
            if (me.options.zScreen && me.options.zScreen.id) {
                selector = _.replace(selector, new RegExp("#", "g"), "#" + me.options.zScreen.id + "_");
            }
        } else if (selector.indexOf(".") == 0) {
            container = me.getContainer();
        }

        var el = $(selector, container);
        return el;
    }

    getContainer(): JQuery {
        return $("#" + this.options.id);
    }

    beforeNavigate(): Promise<boolean> {
        return new Promise(resolve => resolve(true));
    }

    onNavigate(): void {
    }

    protected preRender(): void {
    }

    protected create(): void {
    }

    protected destroy(): void {
    }

    private applyFieldVisibilitySettings(): void {
        var me = this;

        var fSettings = _.filter(fieldVisibilitySettings, function (setting: any) {
            if (setting.EntityType == me.options.screenEntityType &&
                (setting.ScreenName == null || setting.ScreenName == me.options.screenName) &&
                (setting.SectionName == null || setting.SectionName == me.options.sectionName) &&
                (setting.SubType == null || setting.SubType == me.options.subType)) {

                let fieldName: string;
                if (typeof me.options.bindValue == "string") {
                    fieldName = me.options.bindValue || me.getId();
                } else {
                    fieldName = (me.options.bindValue ? me.options.bindValue.value : null) || me.getId();
                }

                return setting.FieldName == fieldName;
            }
            return false;
        });

        if (fSettings.length > 0 || this.recursiveDisable) {

            var required;
            var disabled = this.recursiveDisable;
            var hidden;

            $.each(fSettings, function (index, setting) {

                if ((setting.Required === true || setting.Required === false) && required !== true) {
                    required = setting.Required;
                }

                if ((setting.EditableOnDesktop === true || setting.EditableOnDesktop === false) && disabled !== true) {
                    disabled = !setting.EditableOnDesktop;
                }

                if ((setting.ShowOnDesktop === true || setting.ShowOnDesktop === false) && hidden !== true) {
                    hidden = !setting.ShowOnDesktop;
                }

            });

            if (required !== undefined) {
                me.options.required = required;
                me.options.classes = me.options.classes + " field-visibility-required";
            }

            if ((disabled !== undefined && disabled != false) && !me.options.excludeFromRecursiveDisable) {
                me.options.disabled = disabled;
                me.options.required = false;
                me.options.containerClasses = me.options.containerClasses + " field-visibility-disabled";
                me.options.classes = me.options.classes + " field-visibility-disabled";
                if (me.options.type == "Container") {
                    this.recursiveDisable = disabled;
                }
            }

            if (hidden !== undefined && hidden) {
                me.options.containerClasses = me.options.containerClasses + " field-visibility-hidden";
                me.options.classes = me.options.classes + " field-visibility-hidden";
                me.options.required = false;
            } else if (hidden !== undefined && !hidden) {
                me.options.containerClasses = me.options.containerClasses || "";
                me.options.containerClasses = me.options.containerClasses.replaceAll("hidden", "");
                me.options.classes = me.options.classes.replaceAll("hidden", "");
            }
        }
    }

    private getId(): string {
        var me = this;
        var screen = me.options.zScreen || {};
        var id = me.options.id + "";
        var screenIdApplied = id.indexOf(screen.id) == 0;

        if (screenIdApplied) {
            id = id.substr((screen.id + "").length + 1);
        }

        if (parseInt(id, 10)) {
            id = (me.options.screenName + "Screen");
        }

        return id;
    }

}
